import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components';
import { SearchableDropdown } from '@/components/SearchableDropdown';
import { SideBarModal } from '@/components/Modal';
import { vehiclesApi, Vehicle } from '@/services/api/vehicles';
import { getListPitstop, Pitstop } from '@/services/api/pitstops';
import { BUSINESS_UNIT_OPTIONS } from '@/types/common';
import { extractApiErrorMessage } from '@/utils';
import { showErrorToast } from '@/components/toast/globalToast';

interface SelectReplacementVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNext: (vehicle: Vehicle) => void;
  isSubmitting?: boolean;
}

const inputClassName = (isDisabled: boolean) =>
  `w-full rounded-md border px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200 ${
    isDisabled ? 'bg-gray-100 text-gray-400' : 'border-gray-200'
  }`;

const SelectReplacementVehicleModal: React.FC<
  SelectReplacementVehicleModalProps
> = ({ isOpen, onClose, onNext, isSubmitting = false }) => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [hubLocations, setHubLocations] = useState<Pitstop[]>([]);
  const [selectedHubId, setSelectedHubId] = useState('');
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHubs, setIsLoadingHubs] = useState(false);

  useEffect(() => {
    const loadHubLocations = async () => {
      setIsLoadingHubs(true);
      try {
        const response = await getListPitstop({
          page: '1',
          size: '10000',
          type: 'HUB',
          hasStaff: true,
        });
        setHubLocations(response.data || []);
      } catch (error) {
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to load hub locations')
        );
        setHubLocations([]);
      } finally {
        setIsLoadingHubs(false);
      }
    };

    if (isOpen) {
      setSelectedHubId('');
      setSelectedBusinessUnit('');
      setSelectedVehicleId('');
      setVehicles([]);
      loadHubLocations();
    }
  }, [isOpen]);

  useEffect(() => {
    const loadVehicles = async () => {
      if (!selectedHubId || !selectedBusinessUnit) {
        setVehicles([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await vehiclesApi.getVehicles(1, 1000, '', {
          statuses: 'IDLE',
          isAvailable: true,
          hubIDs: selectedHubId,
          businessUnits: selectedBusinessUnit,
        });
        setVehicles(response.data || []);
      } catch (error) {
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to load vehicles')
        );
        setVehicles([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      setSelectedVehicleId('');
      loadVehicles();
    }
  }, [isOpen, selectedHubId, selectedBusinessUnit]);

  const hubLocationOptions = useMemo(
    () =>
      hubLocations.map((hub) => ({
        value: String(hub.ID ?? hub.id),
        label: hub.name || '',
      })),
    [hubLocations]
  );

  const vehicleOptions = useMemo(
    () =>
      vehicles.map((vehicle) => ({
        value: String(vehicle.ID ?? vehicle.id),
        label: `${vehicle.platNomor} - ${vehicle.oemName || '-'} - ${
          vehicle.modelName || '-'
        }`,
      })),
    [vehicles]
  );

  const selectedVehicle = useMemo(() => {
    if (!selectedVehicleId) return null;
    return vehicles.find(
      (vehicle) => String(vehicle.ID ?? vehicle.id) === selectedVehicleId
    );
  }, [selectedVehicleId, vehicles]);

  const handleNext = () => {
    if (!selectedVehicle) return;
    onNext(selectedVehicle);
  };

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      width="sm"
      maxHeight="screen"
      showCloseButton
      className="max-w-[420px]"
      header={
        <div>
          <div className="text-base font-semibold text-gray-900">
            Select Replacement Vehicle
          </div>
          <div className="text-xs text-gray-500">Subtitle</div>
        </div>
      }
      footer={
        <div className="flex w-full justify-end">
          <Button
            onClick={handleNext}
            disabled={!selectedVehicle || isSubmitting}
            className="w-full"
            variant="primary"
          >
            {isSubmitting ? 'Saving...' : 'Next'}
          </Button>
        </div>
      }
    >
      <div className="bg-gray-50 px-4 py-2 text-[11px] font-medium text-gray-500 rounded-md">
        AVAILABLE VEHICLES
      </div>

      <div className="px-6 py-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hub Location <span className="text-red-500">*</span>
          </label>
          <SearchableDropdown
            placeholder={
              isLoadingHubs ? 'Loading hubs...' : 'Select hub location'
            }
            options={hubLocationOptions}
            value={selectedHubId}
            onChange={(value) => setSelectedHubId(value as string)}
            disabled={isLoadingHubs}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Unit <span className="text-red-500">*</span>
          </label>
          <SearchableDropdown
            placeholder="Select business unit"
            options={BUSINESS_UNIT_OPTIONS}
            value={selectedBusinessUnit}
            onChange={(value) => setSelectedBusinessUnit(value as string)}
            disabled={!selectedHubId}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Plate Number <span className="text-red-500">*</span>
          </label>
          <SearchableDropdown
            placeholder={
              !selectedHubId || !selectedBusinessUnit
                ? 'Select Hub Location and Business Unit first'
                : isLoading
                  ? 'Loading vehicles...'
                  : 'e.g. B 1234 ABC - Electrum - H3'
            }
            options={vehicleOptions}
            value={selectedVehicleId}
            onChange={(value) => setSelectedVehicleId(value as string)}
            disabled={!selectedHubId || !selectedBusinessUnit || isLoading}
          />
        </div>
      </div>

      <div className="bg-gray-50 px-4 py-2 text-[11px] font-medium text-gray-500 rounded-md">
        VEHICLE DETAILS
      </div>

      <div className="px-6 py-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Plate Number
          </label>
          <input
            value={selectedVehicle?.platNomor || ''}
            placeholder="e.g. B 1234 ABC"
            disabled
            className={inputClassName(true)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nomor Rangka / VIN
          </label>
          <input
            value={selectedVehicle?.vin || ''}
            placeholder="e.g. MHJBA111ARJ000846"
            disabled
            className={inputClassName(true)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nomor Mesin
          </label>
          <input
            value={selectedVehicle?.imei || ''}
            placeholder="e.g. 14ZW6067328YA0323G0321"
            disabled
            className={inputClassName(true)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            OEM Name
          </label>
          <input
            value={selectedVehicle?.oemName || ''}
            placeholder="e.g. Smoot"
            disabled
            className={inputClassName(true)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Model Name
          </label>
          <input
            value={selectedVehicle?.modelName || ''}
            placeholder="e.g. Tempur"
            disabled
            className={inputClassName(true)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <input
            value={selectedVehicle?.status || ''}
            placeholder="e.g. IDLE"
            disabled
            className={inputClassName(true)}
          />
        </div>
      </div>
    </SideBarModal>
  );
};

export default SelectReplacementVehicleModal;
